import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API, formatCurrency, formatDate, INVOICE_STATUS_LABELS, INVOICE_STATUS_COLORS } from '../lib/utils';
import { toast } from 'sonner';
import { Search, Receipt, ChevronRight, Filter, FileText } from 'lucide-react';

const InvoicesPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des factures');
    }
    setLoading(false);
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="page-container p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-12 bg-muted rounded"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">Factures</h1>
        </div>

        {/* Info Card */}
        <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              Les factures sont créées à partir des devis acceptés. Allez sur un devis accepté et cliquez sur "Convertir en facture".
            </p>
          </CardContent>
        </Card>

        {/* Search & Filter */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
              data-testid="invoice-search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 h-12 bg-muted/50 border-transparent" data-testid="invoice-filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="brouillon">Brouillon</SelectItem>
              <SelectItem value="envoyee">Envoyée</SelectItem>
              <SelectItem value="payee">Payée</SelectItem>
              <SelectItem value="en_retard">En retard</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Invoice List */}
        {filteredInvoices.length > 0 ? (
          <div className="space-y-3">
            {filteredInvoices.map((invoice) => (
              <Card 
                key={invoice.id} 
                className="bg-card border-white/10 card-hover cursor-pointer"
                onClick={() => navigate(`/invoices/${invoice.id}`)}
                data-testid={`invoice-card-${invoice.id}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-sm text-muted-foreground">
                        {invoice.invoice_number}
                      </span>
                      <span className={`status-badge ${INVOICE_STATUS_COLORS[invoice.status]}`}>
                        {INVOICE_STATUS_LABELS[invoice.status]}
                      </span>
                      {invoice.quote_number && (
                        <span className="text-xs text-muted-foreground">
                          (Devis {invoice.quote_number})
                        </span>
                      )}
                    </div>
                    <p className="font-semibold truncate">{invoice.client_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Créée le {formatDate(invoice.created_at)} • Échéance {formatDate(invoice.payment_due_date)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{formatCurrency(invoice.total_ttc)}</p>
                    <p className="text-xs text-muted-foreground">TTC</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-white/10">
            <CardContent className="empty-state">
              <Receipt className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {searchTerm || filterStatus !== 'all' ? 'Aucun résultat' : 'Aucune facture'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Essayez avec d\'autres critères' 
                  : 'Les factures sont créées à partir de devis acceptés'}
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <Button
                  onClick={() => navigate('/quotes')}
                  className="bg-primary text-primary-foreground hover:bg-accent rounded-full"
                  data-testid="go-to-quotes-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Voir mes devis
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InvoicesPage;
